/* eslint-disable sonarjs/no-duplicate-string */
import { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { EUFlagIcon } from './icons/EUFlagIcon';
import { USFlagIcon } from './icons/USFlagIcon';
import { Badge } from './common/Badge';
import { BrandHeader } from './BrandHeader';
import { Button } from './common/buttons/Button';
import { InformationChip } from './common/chips/InformationChip';
import { RadioGroup } from './common/radio/RadioGroup';
import { StyledPageInnerContainer, StyledPageOuterContainer } from './layout/PageComponents';
import {
    StyledBorderBox,
    StyledBorderBoxContent,
    StyledBorderBoxHeader,
    StyledBorderBoxHeaderColumn,
    StyledBorderBoxHeaderRow,
} from './layout/BorderBoxComponents';

type Realm = 'eu' | 'us';

export interface RealmSelectorProps {
    euAppUrl: string;
    queryParams?: string;
    urlPath?: string;
    usAppUrl: string;
}

const StyledOptionLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const StyledOptionTitleRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    '& .MuiSvgIcon-root': {
        flexShrink: 0,
        height: 24,
        width: 24,
    },
}));

const StyledOptionTitle = styled(Typography)(({ theme }) => ({
    margin: 0,
    marginTop: theme.spacing(-0.25),
}));

const StyledOptionDescription = styled(Typography)(({ theme }) => ({
    margin: 0,
    paddingLeft: `calc(24px + ${theme.spacing(2)})`,
}));

export const RealmSelector: React.FC<RealmSelectorProps> = ({ euAppUrl, queryParams = '', urlPath = '', usAppUrl }) => {
    const [realm, setRealm] = useState<Realm>('eu');

    const realmUrl = realm === 'us' ? usAppUrl : euAppUrl;
    const redirectionUrl = `${realmUrl}${urlPath}${queryParams}`;

    return (
        <>
            <BrandHeader />
            <StyledPageOuterContainer>
                <StyledPageInnerContainer maxWidth={752}>
                    <StyledBorderBox>
                        <StyledBorderBoxHeader>
                            <StyledBorderBoxHeaderColumn>
                                <StyledBorderBoxHeaderRow>
                                    <Typography variant="h6">Where should your data be hosted?</Typography>
                                    <Badge icon={<PublicOutlinedIcon />} size="small" />
                                </StyledBorderBoxHeaderRow>
                                <Typography color="text.secondary" fontWeight="normal" variant="subtitle1">
                                    Choose the region that best fits your business, customers and data residency
                                    requirements.
                                </Typography>
                            </StyledBorderBoxHeaderColumn>
                        </StyledBorderBoxHeader>
                        <Divider />
                        <StyledBorderBoxContent>
                            <RadioGroup
                                buttons={[
                                    {
                                        endIcon: <InformationChip label="Default" severity="grey" />,
                                        label: (
                                            <StyledOptionLabel>
                                                <StyledOptionTitleRow>
                                                    <EUFlagIcon />
                                                    <StyledOptionTitle>European Union</StyledOptionTitle>
                                                </StyledOptionTitleRow>
                                                <StyledOptionDescription color="text.secondary" variant="body2">
                                                    Your data will be hosted within the EU.
                                                </StyledOptionDescription>
                                            </StyledOptionLabel>
                                        ),
                                        value: 'eu',
                                    },
                                    {
                                        label: (
                                            <StyledOptionLabel>
                                                <StyledOptionTitleRow>
                                                    <USFlagIcon />
                                                    <StyledOptionTitle>United States</StyledOptionTitle>
                                                </StyledOptionTitleRow>
                                                <StyledOptionDescription color="text.secondary" variant="body2">
                                                    Your data will be hosted within the US.
                                                </StyledOptionDescription>
                                            </StyledOptionLabel>
                                        ),
                                        value: 'us',
                                    },
                                ]}
                                layout="list"
                                size="medium"
                                value={realm}
                                onChange={(value) => setRealm(value as Realm)}
                            />
                        </StyledBorderBoxContent>
                    </StyledBorderBox>
                    <Button link={{ href: redirectionUrl, type: 'external' }}>{`Continue with the ${
                        realm === 'eu' ? 'European Union' : 'United States'
                    }`}</Button>
                </StyledPageInnerContainer>
            </StyledPageOuterContainer>
        </>
    );
};
